import Modal from '@ui/modal/modal';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { AUDIENCE_CARD_TYPE, AudienceAddProps, GateTypes, PasscodeList, Requirement } from '@/types/registration.types';
import SearchInput from '@ui/search-input/search-input';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getWorkingProjectSessions, selectWorkingProjectRegistrationSettings } from '@/store/slices/project/selectors';
import { NavLink } from 'react-router-dom';
import OptionalComponent from '@ui/optional-component/optional-component';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { selectProjectsList } from '@/store/slices/projects/selectors';
import useFetchProjectsList from '@/micros/homepage/projects/utils/use-fetch-projects-list';
import { useProjectUuidParam } from '@/utils/use-project-uuid-param';
import AudienceAddRow from '../new-audiences/audience-card-add';
import CloneLists from '@/connection/audiences/clone-lists';
import useChannelRegistrantCounts from '@/connection/hooks/use-channel-registrant-counts';
import { getActiveChannel } from '@/store/slices/user/selectors';
import toastTrigger from '@ui/toast/toast-trigger';
import { Virtuoso } from 'react-virtuoso';

type SelectListModalProps = {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  passcodeLists: PasscodeList[];
};

type RegistrantListToAdd = Record<string, string[]>;

const SelectListModal = ({ open, setOpen, passcodeLists }: SelectListModalProps) => {
  const channel = useAppSelector(getActiveChannel);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const sessions = useAppSelector(getWorkingProjectSessions);
  const projects = useAppSelector(selectProjectsList);
  const projectUuidParam = useProjectUuidParam();

  const [searchValue, setSearchValue] = useState('');
  const [passcodeListsToAdd, setPasscodeListsToAdd] = useState<number[]>([]);
  const [registrantListsToAdd, setRegistrantListsToAdd] = useState<RegistrantListToAdd>({});
  const [filteredProjectAudienceLists, setFilteredProjectAudienceLists] = useState<AudienceAddProps[]>([]);
  const [saving, setSaving] = useState(false);

  const channelRegistrantCounts = useChannelRegistrantCounts(open);
  const dispatch = useAppDispatch();
  useFetchProjectsList();

  useEffect(() => {
    const passcodeListAudienceAddProps: AudienceAddProps[] = passcodeLists.flatMap((passcodeList) => {
      if ((passcodeList.member_count ?? 0) === 0) return [];
      return [
        {
          name: passcodeList.name,
          description: passcodeList.description,
          audienceType: AUDIENCE_CARD_TYPE.PASSCODE_LIST,
          passcodeListId: passcodeList.passcode_list,
          cloned_source: passcodeList.cloned_source,
          member_count: passcodeList.member_count,
        },
      ];
    });

    const projectRegistrantListProps: AudienceAddProps[] = projects.flatMap((project) => {
      if (project.uuid === projectUuidParam) return [];
      if ((channelRegistrantCounts?.[project.uuid]?.total ?? 0) === 0) return [];

      return [
        {
          name: project.name,
          passcodeListId: project.event,
          audienceType: AUDIENCE_CARD_TYPE.ALL_PROFILES,
          registrant_counts: channelRegistrantCounts?.[project.uuid],
          project_uuid: project.uuid,
        },
      ];
    });

    const allAudienceAddProps: AudienceAddProps[] = [...projectRegistrantListProps, ...passcodeListAudienceAddProps];

    const filteredAudienceAddProps = allAudienceAddProps.filter((audience) =>
      audience.name?.toLowerCase().includes(searchValue.toLowerCase())
    );

    setFilteredProjectAudienceLists(filteredAudienceAddProps || []);
  }, [channelRegistrantCounts, passcodeLists, projectUuidParam, projects, searchValue]);

  const handleSearch = useCallback((search: string) => {
    setSearchValue(search.toLowerCase());
  }, []);

  const renderHeaderContent = useCallback(
    () => (
      <Container className="flex-column gap-8" borderRadius="16px">
        <Text className="H4">Audience Lists</Text>
        <Text className="P1" color="var(--gray-500)">
          Select an audience list(s) from your channel
        </Text>
        <Container className="padding-b-16">
          <SearchInput onSubmit={handleSearch} debounceTime={300} onClear={() => handleSearch('')} />
        </Container>
      </Container>
    ),
    [handleSearch]
  );

  const handleClose = () => {
    setPasscodeListsToAdd([]);
    setRegistrantListsToAdd({});
    setSearchValue('');
    setOpen(false);
    setSaving(false);
  };

  const toggleRegistrantListToAdd = (projectUuid: string, audienceTypes: AUDIENCE_CARD_TYPE[]) => {
    setRegistrantListsToAdd((prev) => {
      const existing = prev[projectUuid] ?? [];
      const isRemoving = audienceTypes.every((t) => existing.includes(t));
      const merged = isRemoving
        ? existing.filter((t) => !audienceTypes.includes(t as AUDIENCE_CARD_TYPE))
        : [...new Set([...existing, ...audienceTypes])];
      if (merged.length === 0) return Object.fromEntries(Object.entries(prev).filter(([key]) => key !== projectUuid));
      return { ...prev, [projectUuid]: merged };
    });
  };

  const toggleSelectToAdd = (passcodeListId: number) => {
    setPasscodeListsToAdd((prevLists) => {
      if (prevLists.includes(passcodeListId)) {
        return prevLists.filter((id) => id !== passcodeListId);
      }
      return [...prevLists, passcodeListId];
    });
  };

  const handleSaveLists = () => {
    if (!registrationSettings || (!passcodeListsToAdd.length && !Object.keys(registrantListsToAdd)?.length)) return;

    setSaving(true);

    if (!channel || !projectUuidParam) return;

    if (Object.keys(registrantListsToAdd)?.length) {
      // if we have lists to clone, use the clone service
      // this will also attach passcode lists that do not require creation of a new list in an effort to minimize bundle updates
      const abortController = new AbortController();
      CloneLists({
        channel,
        projectUuid: projectUuidParam,
        registrantListsToAdd: registrantListsToAdd,
        passcodeListToClone: null,
        listsToAdd: passcodeListsToAdd,
        addListsToProject: true,
        signal: abortController.signal,
      })
        .then((response) => {
          toastTrigger(response.message, { type: 'info', autoClose: 5000 });
        })
        .catch((e) => {
          console.error(e);
          toastTrigger('There was a problem cloning the user lists', { type: 'error', autoClose: 5000 });
        });
    } else if (passcodeListsToAdd.length) {
      // we are only adding passcode lists that already exist
      // update and save the event
      const sessionIds = sessions?.map((session) => session.session) || [];
      const newWorkingGateRequirements: Requirement[] = [];

      const requirementsCopy = { ...registrationSettings?.requirements };
      const listsCopy = [...(requirementsCopy?.[GateTypes.passcode_list] || [])];
      const existingIds = listsCopy.flatMap((r) => r.passcodeList ?? []);

      passcodeListsToAdd.forEach((passcodeList) => {
        if (existingIds.includes(passcodeList)) return;

        newWorkingGateRequirements.push({
          type: GateTypes.passcode_list,
          passcodeList: [passcodeList],
          gatedSessions: [...sessionIds],
        });
      });

      listsCopy.push(...newWorkingGateRequirements);

      dispatch(
        saveWorkingProjectByFields({
          saveId: 'passcode-list-added',
          registration_settings: {
            ...registrationSettings,
            requirements: {
              ...registrationSettings.requirements,
              ...(listsCopy.length ? { [GateTypes.passcode_list]: listsCopy } : {}),
            },
          },
        })
      );
    }

    handleClose();
  };

  return (
    <Modal
      open={open}
      onRequestClose={handleClose}
      customSize={{
        width: '600px',
        height: filteredProjectAudienceLists.length > 0 ? '600px' : '400px',
        minHeight: '300px',
        maxHeight: 'calc(100% - 32px)',
        maxWidth: 'calc(100% - 32px)',
      }}
      contentWrapperClassName="padding-h-40 padding-v-24 overflow-y-auto flex-1 flex-column"
      backgroundColor="var(--gray-900)"
      RenderHeaderContent={renderHeaderContent}
      RenderFooterContent={() => (
        <Container background="var(--gray-900)" className="flex fd-row jc-flex-end ai-flex-end gap-16 margin-t-24">
          <Button
            onClick={handleClose}
            backgroundColor="var(--gray-600)"
            hoverBackgroundColor="var(--gray-500)"
            className="L1 padding-v-16 padding-h-24"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveLists}
            className="padding-v-16 padding-h-24"
            disabledOpacity={0.6}
            backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
            disabled={saving || (passcodeListsToAdd.length === 0 && Object.keys(registrantListsToAdd)?.length === 0)}
          >
            <Text
              className="L1"
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            >
              Save
            </Text>
          </Button>
        </Container>
      )}
    >
      <Container className="flex-column" height="100%">
        <OptionalComponent display={filteredProjectAudienceLists.length > 0}>
          <Virtuoso
            style={{ height: '100%' }}
            data={filteredProjectAudienceLists}
            computeItemKey={(_, audience) =>
              audience.project_uuid ? `project-${audience.project_uuid}` : `passcode-${audience.passcodeListId}`
            }
            itemContent={(_, audience) => (
              <Container padding="4px 0">
                <AudienceAddRow
                  audience={audience}
                  onAddAudience={toggleSelectToAdd as Dispatch<SetStateAction<number>>}
                  onAddRegistrantList={toggleRegistrantListToAdd}
                  audienceSelected={
                    audience.passcodeListId ? passcodeListsToAdd.includes(audience.passcodeListId) : false
                  }
                  selectedRegistrantLists={(audience.project_uuid && registrantListsToAdd[audience.project_uuid]) || []}
                />
              </Container>
            )}
          />
        </OptionalComponent>
        <OptionalComponent display={filteredProjectAudienceLists.length === 0}>
          <Container className="flex-column gap-16 ai-center jc-center padding-v-32">
            <Text className="P1" color="var(--gray-500)">
              No lists available. Create a list in your channel to get started.
            </Text>
            <NavLink to="/admin/people" onClick={handleClose}>
              <Button
                className="padding-v-16 padding-h-24"
                disabledOpacity={0.6}
                backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
              >
                <Text
                  className="L1"
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                >
                  Manage lists
                </Text>
              </Button>
            </NavLink>
          </Container>
        </OptionalComponent>
      </Container>
    </Modal>
  );
};

export default SelectListModal;
